import { localize, type FlowDefinition, type FlowStepDefinition } from '@neuroedge/shared';

const KIND_LABEL: Record<FlowStepDefinition['kind'], string> = {
  SEND_MESSAGE: 'Send message',
  SEND_IMAGE: 'Send image',
  LIST_SERVICES: 'List bookable services',
  LIST_SLOTS: 'List open time slots',
  LIST_DATES: 'List available dates',
  CREATE_BOOKING: 'Create the booking',
  LIST_MY_APPOINTMENTS: "List the customer's bookings",
  CANCEL_APPOINTMENT: 'Cancel the booking',
  LIST_PRODUCTS: 'List orderable products',
  SEARCH_PRODUCTS: 'Search or type the order',
  SHOW_PRODUCTS: 'Show catalog products (WhatsApp cart)',
  PLACE_ORDER: 'Place the order',
  LIST_MY_ORDERS: "List the customer's orders",
  CANCEL_ORDER: 'Cancel the order',
  COLLECT_QUOTATION: 'Collect items and send a quotation',
  CREATE_LEAD: 'Capture a lead',
  SHOW_PLANS: 'Show our pricing plans',
  COLLECT_TEXT: 'Ask a question and save the reply',
  SEND_FORM: 'Send a WhatsApp form',
  LANGUAGE_PICKER: "Ask the customer's language",
  END: 'End',
};

/** Previews always show the English (or first-authored) text, same as `localize(value, undefined)` - this is a flow-builder preview, not a live send, so it doesn't know any contact's actual language. */
function summaryText(step: FlowStepDefinition): string {
  switch (step.kind) {
    case 'SEND_MESSAGE':
      return localize(step.text, undefined);
    case 'SEND_IMAGE':
      return step.caption ? localize(step.caption, undefined) : '(image, no caption)';
    case 'LIST_SERVICES':
    case 'LIST_SLOTS':
    case 'LIST_DATES':
    case 'LIST_PRODUCTS':
    case 'SEARCH_PRODUCTS':
    case 'SHOW_PRODUCTS':
    case 'LIST_MY_APPOINTMENTS':
    case 'LIST_MY_ORDERS':
      return localize(step.prompt, undefined);
    case 'COLLECT_QUOTATION':
      return localize(step.prompt, undefined);
    case 'COLLECT_TEXT':
      return localize(step.prompt, undefined);
    case 'SEND_FORM':
      return `${localize(step.text, undefined)} [${localize(step.buttonLabel, undefined)}]`;
    case 'SHOW_PLANS':
      return `${localize(step.prompt, undefined)} (then the plans, with live prices)`;
    case 'LANGUAGE_PICKER':
      return localize(step.prompt, undefined);
    case 'CREATE_BOOKING':
    case 'PLACE_ORDER':
    case 'CANCEL_APPOINTMENT':
    case 'CANCEL_ORDER':
    case 'CREATE_LEAD':
      return `On success: "${localize(step.successText, undefined)}"`;
    case 'END':
      return step.text ? localize(step.text, undefined) : '(ends the conversation with no message)';
  }
}

function edges(step: FlowStepDefinition): { label: string; target: string }[] {
  switch (step.kind) {
    case 'SEND_MESSAGE':
      return Object.entries(step.transitions ?? {}).map(([reply, target]) => ({
        label: `on "${reply}"`,
        target,
      }));
    case 'LIST_SERVICES':
    case 'LIST_SLOTS':
    case 'LIST_DATES':
    case 'LIST_PRODUCTS':
      return [{ label: 'on selection', target: step.next }];
    case 'SHOW_PRODUCTS':
      return [
        { label: 'cart received', target: step.next },
        { label: 'no catalog / type instead', target: step.onUnavailable },
      ];
    case 'SEARCH_PRODUCTS':
      return [
        { label: 'added to cart', target: step.next },
        ...(step.onBrowse ? [{ label: '"Browse all"', target: step.onBrowse }] : []),
      ];
    case 'LIST_MY_APPOINTMENTS':
    case 'LIST_MY_ORDERS':
      return [
        { label: 'on selection', target: step.next },
        { label: 'if none yet', target: step.onEmpty },
      ];
    case 'CREATE_BOOKING':
      return [
        { label: 'on success', target: step.onSuccess },
        { label: 'on failure', target: step.onFailure },
      ];
    case 'CANCEL_APPOINTMENT':
      return [
        { label: 'on success', target: step.onSuccess },
        { label: 'if not found', target: step.onNotFound },
      ];
    case 'PLACE_ORDER':
      return [
        { label: 'on success', target: step.onSuccess },
        { label: 'on empty cart', target: step.onEmpty },
      ];
    case 'CANCEL_ORDER':
      return [
        { label: 'on success', target: step.onSuccess },
        { label: 'if not cancellable', target: step.onNotCancellable },
      ];
    case 'COLLECT_QUOTATION':
      return [
        { label: 'on success', target: step.onSuccess },
        { label: "couldn't quote", target: step.onNoItems },
      ];
    case 'CREATE_LEAD':
    case 'LANGUAGE_PICKER':
    case 'SEND_IMAGE':
    case 'COLLECT_TEXT':
    case 'SEND_FORM':
    case 'SHOW_PLANS':
      return [{ label: 'next', target: step.next }];
    case 'END':
      return [];
  }
}

/**
 * Read-only, step-by-step rendering of a flow's graph - not a drag-and-drop
 * canvas (out of scope, see PROGRESS.md), but enough to actually *see* what a
 * flow does without testing it live on WhatsApp. Used on both the admin
 * flow form/list and anywhere a template's `START_FLOW` action should be
 * explained (e.g. the Templates page).
 */
export function FlowStepsPreview({ definition }: { definition: FlowDefinition }) {
  const ids = Object.keys(definition.steps).sort((a, b) =>
    a === definition.startStepId ? -1 : b === definition.startStepId ? 1 : a.localeCompare(b),
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {ids.map((id) => {
        const step = definition.steps[id]!;
        const isStart = id === definition.startStepId;
        return (
          <div
            key={id}
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md, 6px)',
              padding: 'var(--space-2) var(--space-3)',
            }}
          >
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
              {isStart ? <span className="ne-badge">start</span> : null}
              <strong>{id}</strong>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                {KIND_LABEL[step.kind]}
              </span>
            </div>
            <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--font-size-sm)' }}>
              {summaryText(step)}
            </p>
            {edges(step).length > 0 ? (
              <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                {edges(step).map((e, i) => (
                  <li key={i} style={{ fontSize: 'var(--font-size-sm)' }}>
                    {e.label} → {e.target === '__END__' ? <em>end</em> : <code>{e.target}</code>}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
